import React, { useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { Box, Slider, Typography, Paper, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { FaArrowLeft } from 'react-icons/fa';
import { FaArrowRight } from 'react-icons/fa';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GrDownload } from 'react-icons/gr';
import { HistoricalLayersService, HistoricalTileSource, HistoricalYear } from '../../../types/historical-layers';
import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';

/**
 * Props for the BaseHistoricalLayerSelector component
 */
export interface BaseHistoricalLayerSelectorProps {
  /**
   * Currently selected year
   */
  selectedYear: number;

  /**
   * Callback when year selection changes
   */
  onYearChange: (year: number, tileSource: HistoricalTileSource) => void;

  /**
   * Service for managing historical layers
   */
  historicalLayersService: HistoricalLayersService;

  /**
   * Whether the selector is disabled
   */
  disabled?: boolean;

  /**
   * Callback to fetch data (returns true if fetch can proceed, false otherwise)
   */
  onFetchData: (forceRefresh: boolean) => Promise<void>;

  /**
   * Whether fetch functionality should be enabled
   */
  canFetch: boolean;

  /**
   * Custom controls to render alongside the timeline title
   */
  customControls?: ReactNode;

  /**
   * Custom message to display when no data is available
   */
  noDataMessage?: string;

  /**
   * Custom submessage to display when no data is available
   */
  noDataSubMessage?: string;

  /**
   * Tooltip for the fetch button
   */
  fetchTooltip?: string;
}

/**
 * Base component for historical layer selection with timeline slider
 * Contains all common logic and UI shared between location-based and mission-based selectors
 */
export const BaseHistoricalLayerSelector: React.FC<BaseHistoricalLayerSelectorProps> = ({
  selectedYear,
  onYearChange,
  historicalLayersService,
  disabled = false,
  onFetchData,
  canFetch,
  customControls,
  noDataMessage = 'No historical imagery available',
  noDataSubMessage,
  fetchTooltip = 'Fetch historical imagery',
}) => {
  const [hasError, setHasError] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [availableYears, setAvailableYears] = useState<HistoricalYear[]>(() => {
    // Initialize with all years to avoid loading state on first render
    return historicalLayersService.getAvailableYears();
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Fetch available years on mount
  useEffect(() => {
    async function fetchAvailableYears() {
      setAvailableYears(historicalLayersService.getAvailableYears());
    }

    fetchAvailableYears();
  }, [historicalLayersService]);

  // Subscribe to historical map years loaded event
  useEffect(() => {
    const handleHistoricalMapYearsLoaded = () => {
      console.log('Historical map years loaded event received, refreshing available years');
      const updatedYears = historicalLayersService.getAvailableYears();
      setAvailableYears(updatedYears);
    };

    EventBus.on(MISSION_EVENTS.HISTORICAL_MAP_LAYERS_LOADED, handleHistoricalMapYearsLoaded);

    return () => {
      EventBus.remove(MISSION_EVENTS.HISTORICAL_MAP_LAYERS_LOADED, handleHistoricalMapYearsLoaded);
    };
  }, [historicalLayersService]);

  // Subscribe to mission created/deleted event
  useEffect(() => {
    const handleMissionCreatedDeleted = () => {
      console.log('Mission created/deleted - set default available years (current year)');
      const updatedYears = historicalLayersService.getAvailableYears();
      setAvailableYears(updatedYears);

      // Reset to current year (most recent year)
      if (updatedYears.length > 0) {
        const currentYear = updatedYears.find((y) => y.isCurrentYear);

        if (currentYear) {
          try {
            const tileSource = historicalLayersService.getTileSourceForYear(currentYear.year);
            onYearChange(currentYear.year, tileSource);
          } catch (error) {
            console.error('Error resetting to current year:', error);
          }
        }
      }
    };

    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, handleMissionCreatedDeleted);

    return () => {
      EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, handleMissionCreatedDeleted);
    };
  }, [historicalLayersService, onYearChange]);

  // Handle fetch data button click
  const handleFetchData = useCallback(
    async (forceRefresh: boolean = false) => {
      try {
        setIsFetching(true);
        setFetchError(null);

        await onFetchData(forceRefresh);

        // Refresh the available years list
        const updatedYears = historicalLayersService.getAvailableYears();
        setAvailableYears(updatedYears);
      } catch (error) {
        console.error('Error fetching historical data:', error);
        setFetchError('Error fetching historical data. Please try again.');
      } finally {
        setIsFetching(false);
      }
    },
    [onFetchData, historicalLayersService],
  );

  // Handle normal refresh
  const handleRefresh = useCallback(() => {
    handleFetchData(false);
  }, [handleFetchData]);

  // Handle force refresh
  const handleForceRefresh = useCallback(() => {
    handleFetchData(true);
  }, [handleFetchData]);

  // Create marks for the slider (one for each available year)
  // Use index-based positioning for equal distribution
  // Reverse the array so oldest year is on the left, newest on the right
  const sliderMarks = useMemo(() => {
    const reversedYears = [...availableYears].reverse();

    // Special case: when only one year, position it on the right
    if (reversedYears.length === 1) {
      return [
        {
          value: 1, // Position on the right side of the slider
          label: reversedYears[0].isCurrentYear ? `${reversedYears[0].year} ★` : `${reversedYears[0].year}`,
        },
      ];
    }

    return reversedYears.map((yearData, index) => ({
      value: index,
      label: yearData.isCurrentYear ? `${yearData.year} ★` : `${yearData.year}`,
    }));
  }, [availableYears]);

  // Get the index of the currently selected year (in reversed array)
  const selectedYearIndex = useMemo(() => {
    const reversedYears = [...availableYears].reverse();
    const index = reversedYears.findIndex((y) => y.year === selectedYear);

    return index >= 0 ? index : 0;
  }, [availableYears, selectedYear]);

  // Calculate slider min/max to ensure single year appears on the right
  const sliderConfig = useMemo(() => {
    if (availableYears.length === 1) {
      // When only one year, make it appear on the right side of the slider
      return {
        min: 0,
        max: 1,
        value: 1, // Position on the right
      };
    }

    return {
      min: 0,
      max: availableYears.length - 1,
      value: selectedYearIndex,
    };
  }, [availableYears.length, selectedYearIndex]);

  // Handle slider change (convert index back to year)
  const handleSliderChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const index = typeof value === 'number' ? value : value[0];

      const reversedYears = [...availableYears].reverse();

      if (index < 0 || index >= reversedYears.length) {
        return;
      }

      const yearData = reversedYears[index];
      const newYear = yearData.year;

      // Don't trigger change if same year is selected
      if (newYear === selectedYear) {
        return;
      }

      try {
        const tileSource = historicalLayersService.getTileSourceForYear(newYear);
        onYearChange(newYear, tileSource);
      } catch (error) {
        console.error('Error getting tile source for year:', newYear, error);
      }
    },
    [availableYears, selectedYear, onYearChange, historicalLayersService],
  );

  // Handle previous year button
  const handlePreviousYear = useCallback(() => {
    if (selectedYearIndex > 0) {
      const reversedYears = [...availableYears].reverse();
      const yearData = reversedYears[selectedYearIndex - 1];

      try {
        const tileSource = historicalLayersService.getTileSourceForYear(yearData.year);
        onYearChange(yearData.year, tileSource);
      } catch (error) {
        console.error('Error getting tile source for year:', yearData.year, error);
      }
    }
  }, [selectedYearIndex, availableYears, historicalLayersService, onYearChange]);

  // Handle next year button
  const handleNextYear = useCallback(() => {
    if (selectedYearIndex < availableYears.length - 1) {
      const reversedYears = [...availableYears].reverse();
      const yearData = reversedYears[selectedYearIndex + 1];

      try {
        const tileSource = historicalLayersService.getTileSourceForYear(yearData.year);
        onYearChange(yearData.year, tileSource);
      } catch (error) {
        console.error('Error getting tile source for year:', yearData.year, error);
      }
    }
  }, [selectedYearIndex, availableYears, historicalLayersService, onYearChange]);

  // Handle error state
  if (hasError) {
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          p: 2,
          px: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="error">
          Error loading historical data
        </Typography>
      </Paper>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          p: 2,
          px: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading imagery data...
        </Typography>
      </Paper>
    );
  }

  // Handle no data available
  if (availableYears.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          p: 2,
          px: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {noDataMessage}
        </Typography>
        {noDataSubMessage && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {noDataSubMessage}
          </Typography>
        )}
      </Paper>
    );
  }

  // Find the year data for displaying additional info
  const currentYearData = availableYears.find((y) => y.year === selectedYear);

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        p: 2,
        px: 4,
        minWidth: 600,
        maxWidth: '90vw',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        borderRadius: 2,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Historical Imagery Timeline
            </Typography>

            {customControls}

            <Tooltip title={fetchTooltip}>
              <IconButton
                onClick={handleRefresh}
                disabled={!canFetch || isFetching}
                size="large"
                aria-label="Fetch Data"
              >
                {isFetching ? <CircularProgress size={32} /> : <GrDownload fontSize="large" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Force refresh (ignore cache and regenerate)">
              <IconButton
                onClick={handleForceRefresh}
                disabled={!canFetch || isFetching}
                size="large"
                aria-label="Force Refresh"
              >
                {isFetching ? <CircularProgress size={32} /> : <RefreshIcon fontSize="large" />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {currentYearData?.displayName || selectedYear}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Previous year">
                <span>
                  <IconButton
                    onClick={handlePreviousYear}
                    disabled={disabled || availableYears.length === 1 || selectedYearIndex === 0}
                    size="large"
                    aria-label="Previous Year"
                    sx={{
                      width: 56,
                      height: 56,
                      '& svg': { fontSize: '2rem' },
                    }}
                  >
                    <FaArrowLeft />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next year">
                <span>
                  <IconButton
                    onClick={handleNextYear}
                    disabled={
                      disabled || availableYears.length === 1 || selectedYearIndex === availableYears.length - 1
                    }
                    size="large"
                    aria-label="Next Year"
                    sx={{
                      width: 56,
                      height: 56,
                      '& svg': { fontSize: '2rem' },
                    }}
                  >
                    <FaArrowRight />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {fetchError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
            {fetchError}
          </Typography>
        )}

        {isFetching && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Fetching historical data...
          </Typography>
        )}

        <Slider
          value={sliderConfig.value}
          onChange={handleSliderChange}
          min={sliderConfig.min}
          max={sliderConfig.max}
          marks={sliderMarks}
          step={null} // Only allow selection of marked years
          disabled={disabled || availableYears.length === 1} // Disable when only one year
          valueLabelDisplay="auto"
          valueLabelFormat={(index) => {
            const reversedYears = [...availableYears].reverse();

            return reversedYears[index]?.year || '';
          }}
          sx={{
            mt: 1,
            '& .MuiSlider-mark': {
              height: 8,
              width: 2,
              backgroundColor: 'currentColor',
            },
            '& .MuiSlider-markLabel': {
              fontSize: '0.75rem',
              top: 28,
            },
            '& .MuiSlider-thumb': {
              width: 20,
              height: 20,
            },
            '& .MuiSlider-rail': {
              opacity: 0.3,
            },
          }}
        />
      </Box>
    </Paper>
  );
};
